import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Drawer, Empty, Input, Select, Spin, Table } from 'antd';
import dayjs from 'dayjs';
import CompensationPanel, {
  COMPENSATION_QUERY_KEY,
} from '@/components/compensation/CompensationPanel';
import {
  ContentBackground,
  ContentBody,
  ContentHeader,
  ContentWrapper,
} from '@/components/content';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import {
  getCompanyCompensationList,
  getCompensationCompanies,
  getEmployeeCompensation,
} from '@/services/compensation.service';

const money = (value) =>
  value === null || value === undefined
    ? '—'
    : Number(value).toLocaleString('sr-RS', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

function EmployeeDrawer({ employeeId, title, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: [COMPENSATION_QUERY_KEY, employeeId],
    queryFn: () => getEmployeeCompensation(employeeId),
    enabled: !!employeeId,
    retry: false,
  });
  return (
    <Drawer open={!!employeeId} onClose={onClose} width={760} title={title}>
      {isLoading ? <Spin /> : <CompensationPanel data={data} />}
    </Drawer>
  );
}

/**
 * Salaries of a company's employees, for those who see them: HLB for its
 * portfolio, and the people the company picked as salary viewers — whatever
 * their role, so also someone who otherwise sees only their own profile.
 */
function CompensationContent() {
  const { t } = useTranslation(['translations', 'menus']);
  const tc = (key, opts) => t(`compensation.${key}`, opts);
  const [companyId, setCompanyId] = useState(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);

  const { data: companies = [], isLoading: companiesLoading } = useQuery({
    queryKey: ['compensation-companies'],
    queryFn: getCompensationCompanies,
  });
  useEffect(() => {
    if (!companyId && companies.length === 1) setCompanyId(companies[0]._id);
  }, [companies, companyId]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['company-compensation', companyId],
    queryFn: () => getCompanyCompensationList(companyId),
    enabled: !!companyId,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? rows.filter((r) =>
          `${r.firstName} ${r.lastName}`.toLowerCase().includes(q)
        )
      : rows;
  }, [rows, search]);

  const columns = [
    {
      title: tc('employee'),
      key: 'name',
      render: (_, r) => `${r.lastName} ${r.firstName}`,
    },
    {
      title: tc('contracted'),
      key: 'salary',
      align: 'right',
      render: (_, r) =>
        r.salary
          ? `${money((r.salary.amount * (r.salary.workTimePercent || 100)) / 100)} ${r.salary.currency}`
          : '—',
    },
    {
      title: <span className="inline-block first-letter:uppercase">{tc('gross1')}</span>,
      key: 'gross1',
      align: 'right',
      render: (_, r) => money(r.salary?.breakdown?.gross1),
    },
    {
      title: <span className="inline-block first-letter:uppercase">{tc('gross2')}</span>,
      key: 'gross2',
      align: 'right',
      render: (_, r) => money(r.salary?.breakdown?.gross2),
    },
    {
      title: tc('fromShort'),
      dataIndex: 'validFrom',
      render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '—'),
    },
    {
      title: tc('lastPayslip'),
      key: 'payslip',
      align: 'right',
      render: (_, r) =>
        r.lastPayslip
          ? `${r.lastPayslip.period.slice(4)}.${r.lastPayslip.period.slice(0, 4)} · ${money(r.lastPayslip.payout)}`
          : '—',
    },
  ];

  const openRow = rows.find((r) => String(r._id) === String(open));

  return (
    <RootLayout metadata={{ title: t('compensation', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />
      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader title={t('compensation', { ns: 'menus' })} />
            <ContentBody>
              <div className="with-y-overflow">
                <div className="grid gap-4 p-4">
                  <p className="m-0 text-sm opacity-70">{tc('pageSubtitle')}</p>
                  <div className="flex flex-wrap gap-3">
                    <Select
                      className="min-w-[280px]"
                      showSearch
                      optionFilterProp="label"
                      loading={companiesLoading}
                      placeholder={tc('pickCompany')}
                      value={companyId}
                      onChange={setCompanyId}
                      options={companies.map((c) => ({
                        value: c._id,
                        label: c.name,
                      }))}
                    />
                    <Input.Search
                      allowClear
                      className="max-w-[280px]"
                      placeholder={tc('searchEmployee')}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  {!companiesLoading && !companies.length ? (
                    <Empty description={tc('noCompanies')} />
                  ) : (
                    <Table
                      size="small"
                      rowKey="_id"
                      loading={isLoading}
                      columns={columns}
                      dataSource={companyId ? filtered : []}
                      pagination={{ pageSize: 50, showSizeChanger: false }}
                      onRow={(r) => ({
                        onClick: () => setOpen(r._id),
                        className: 'cursor-pointer',
                      })}
                    />
                  )}
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>
      <EmployeeDrawer
        employeeId={open}
        title={openRow ? `${openRow.firstName} ${openRow.lastName}` : ''}
        onClose={() => setOpen(null)}
      />
    </RootLayout>
  );
}

// Every signed-in role may open it (the role check refuses an empty role list,
// hence the bypass): what it lists is decided by the server — the HLB
// portfolio, or companies that picked the caller as a salary viewer.
const CompensationPage = withPrivateRoute(CompensationContent, {
  bypassAuthCheck: true,
});

export default CompensationPage;
